import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Button } from '../UI';
import {
  User, Plus, Edit2, Trash2, X, Check, AlertTriangle,
  ChevronDown, ChevronUp, Search, Camera, Leaf,
  BookOpen, Package, Pencil, Users,
} from 'lucide-react';
import { useCareProfiles }      from '../../hooks/useCareProfiles';
import { usePantryCollections } from '../../hooks/usePantryCollections';
import { useAuth }              from '../../hooks/useAuth';
import { getRecipeImageURL }    from '../../hooks/useRecipes';
import { ingredientSearchService } from '../../services/ingredientSearchService';

// ---------------------------------------------------------------------------
// Constants — raw values MUST match iOS DietaryTag enum raw values exactly
// ---------------------------------------------------------------------------

const DIETARY_TAGS = [
  { id: 'vegan',        name: 'Vegan',        color: '#22c55e' },
  { id: 'vegetarian',   name: 'Vegetarian',   color: '#86efac' },
  { id: 'gluten_free',  name: 'Gluten-Free',  color: '#f97316' },
  { id: 'dairy_free',   name: 'Dairy-Free',   color: '#f97316' },
  { id: 'nut_free',     name: 'Nut-Free',     color: '#f97316' },
  { id: 'keto',         name: 'Keto',         color: '#3b82f6' },
  { id: 'paleo',        name: 'Paleo',        color: '#3b82f6' },
  { id: 'low_carb',     name: 'Low Carb',     color: '#3b82f6' },
  { id: 'halal',        name: 'Halal',        color: '#7c3aed' },
  { id: 'kosher',       name: 'Kosher',       color: '#7c3aed' },
  { id: 'pescatarian',  name: 'Pescatarian',  color: '#06b6d4' },
  { id: 'low_fodmap',   name: 'Low FODMAP',   color: '#ec4899' },
];

const COMMON_ALLERGENS = [
  'Peanuts', 'Tree Nuts', 'Milk', 'Eggs', 'Soy',
  'Wheat', 'Fish', 'Shellfish', 'Sesame',
];

const RELATIONSHIPS = [
  'Self', 'Partner', 'Child', 'Parent', 'Sibling',
  'Grandparent', 'Friend', 'Client', 'Patient', 'Other',
];

const AGE_GROUPS = [
  { label: 'Infant', subtitle: '0–2'   },
  { label: 'Child',  subtitle: '3–12'  },
  { label: 'Teen',   subtitle: '13–17' },
  { label: 'Adult',  subtitle: '18–64' },
  { label: 'Senior', subtitle: '65+'   },
];

// Map age group label → representative birth year
const ageGroupToBirthYear = (group) => {
  const year = new Date().getFullYear();
  const map  = { Infant: 1, Child: 8, Teen: 14, Adult: 30, Senior: 65 };
  return group ? year - (map[group] || 30) : null;
};

// Map birth year → age group label
const birthYearToAgeGroup = (year) => {
  if (!year) return null;
  const age = new Date().getFullYear() - year;
  if (age <= 2)  return 'Infant';
  if (age <= 12) return 'Child';
  if (age <= 17) return 'Teen';
  if (age <= 64) return 'Adult';
  return 'Senior';
};

// Deterministic avatar color from name — matches iOS avatarColor
const getAvatarColor = (name = '') => {
  const COLORS = ['#2D5F4D','#8B6F47','#06B6D4','#7C3AED','#D97706','#DC2626','#ec4899','#22c55e'];
  const hash   = Math.abs(name.split('').reduce((a, b) => a + b.charCodeAt(0), 0));
  return COLORS[hash % COLORS.length];
};

// ---------------------------------------------------------------------------
// Portal Modal
// ---------------------------------------------------------------------------

const Modal = ({ children, onBackdropClick }) => {
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-lg overflow-y-auto"
      onClick={onBackdropClick}
    >
      <div onClick={e => e.stopPropagation()}>{children}</div>
    </div>,
    document.body
  );
};

// ---------------------------------------------------------------------------
// FormCard — matches iOS formCard
// ---------------------------------------------------------------------------

const FormCard = ({ title, icon: Icon, tint = 'var(--color-primary)', children }) => (
  <div
    className="rounded-large bg-white p-lg shadow-sm"
    style={{ border: '1px solid rgba(var(--color-primary-rgb),0.07)', boxShadow: '0 3px 8px rgba(0,0,0,0.04)' }}
  >
    <div className="flex items-center gap-sm mb-lg">
      <div
        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${tint}1A` }}
      >
        <Icon size={13} style={{ color: tint }} />
      </div>
      <p className="text-callout font-bold text-text-primary">{title}</p>
    </div>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// AvatarCircle — colored initials circle, matches iOS CareProfileImage fallback
// ---------------------------------------------------------------------------

const AvatarCircle = ({ name, size = 52, imageURL }) => {
  const color   = getAvatarColor(name);
  const initial = (name || '?').charAt(0).toUpperCase();

  if (imageURL) {
    return (
      <div
        className="rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white"
        style={{ width: size, height: size }}
      >
        <img src={imageURL} alt={name} className="w-full h-full object-cover" />
      </div>
    );
  }

  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0 ring-2 ring-white"
      style={{
        width:      size,
        height:     size,
        background: `linear-gradient(135deg, ${color}4D, ${color}1F)`,
      }}
    >
      <span
        className="font-bold"
        style={{ fontSize: size * 0.38, color }}
      >
        {initial}
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// AssignRecipesPicker — modal for selecting recipes to assign
// ---------------------------------------------------------------------------

const AssignRecipesPicker = ({ assignedIds, onDone, onClose }) => {
  const { user } = useAuth();
  const [allRecipes, setAllRecipes] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [selected, setSelected]     = useState(new Set(assignedIds || []));

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const load = async () => {
      try {
        // Load user's favorited recipes — mirrors iOS savedRecipes
        const snap = await getDocs(
          query(collection(db, 'userFavoriteRecipes'), where('userId', '==', user.uid))
        );
        const recipeIds = snap.docs.map(d => d.data().recipeId).filter(Boolean);
        if (!recipeIds.length) { setLoading(false); return; }

        const batches = [];
        for (let i = 0; i < recipeIds.length; i += 10) batches.push(recipeIds.slice(i, i + 10));
        const loaded = [];
        for (const batch of batches) {
          const rSnap = await getDocs(
            query(collection(db, 'recipes'), where('__name__', 'in', batch))
          );
          rSnap.docs.forEach(d => loaded.push({ ...d.data(), firestoreId: d.id }));
        }
        setAllRecipes(loaded.filter(r => r.status === 'approved' && r.isVisible !== false));
      } catch (err) { console.error('AssignRecipesPicker:', err); }
      finally { setLoading(false); }
    };
    load();
  }, [user]);

  const filtered = allRecipes.filter(r =>
    !search.trim() || (r.title || '').toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <div className="bg-background rounded-large border border-text-tertiary/20 w-full max-w-lg max-h-[80vh] flex flex-col shadow-xl">
      <div className="p-lg border-b border-text-tertiary/10 flex items-center justify-between flex-shrink-0">
        <div>
          <h3 className="text-title-3 font-semibold text-primary">Assign Recipes</h3>
          <p className="text-caption text-text-secondary mt-xs">{selected.size} selected</p>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center">
          <X size={18} className="text-text-tertiary" />
        </button>
      </div>
      <div className="p-lg border-b border-text-tertiary/10 flex-shrink-0">
        <div className="relative">
          <Search size={15} className="absolute left-md top-1/2 -translate-y-1/2 text-text-tertiary" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search recipes..." autoFocus
            className="w-full pl-xxxl pr-md py-sm bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-lg">
        {loading ? (
          <div className="text-center py-xl"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-xl">
            <BookOpen size={32} className="text-text-tertiary/30 mx-auto mb-md" />
            <p className="text-callout font-semibold text-text-secondary mb-xs">No saved recipes</p>
            <p className="text-caption text-text-tertiary">Favourite recipes in the Recipes tab to assign them here.</p>
          </div>
        ) : (
          <div className="space-y-sm">
            {filtered.map(recipe => {
              const id  = recipe.firestoreId;
              const sel = selected.has(id);
              const img = getRecipeImageURL(recipe);
              return (
                <button key={id} onClick={() => toggle(id)}
                  className={`w-full flex items-center gap-md p-md rounded-large border transition-all text-left ${sel ? 'border-primary/30 bg-primary/5' : 'border-text-tertiary/10 hover:border-text-tertiary/20'}`}>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${sel ? 'border-primary bg-primary' : 'border-text-tertiary/30'}`}>
                    {sel && <Check size={11} className="text-white" />}
                  </div>
                  <div className="w-10 h-10 rounded-medium overflow-hidden bg-surface flex-shrink-0">
                    {img ? <img src={img} alt={recipe.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><BookOpen size={16} className="text-text-tertiary" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-callout font-semibold text-text-primary truncate">{recipe.title}</p>
                    <p className="text-caption text-text-secondary capitalize">{recipe.mealType}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="p-lg border-t border-text-tertiary/10 flex gap-md flex-shrink-0">
        <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button variant="primary" onClick={() => { onDone(Array.from(selected)); onClose(); }} className="flex-1">
          Done ({selected.size})
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// AssignPantriesPicker — modal for selecting pantry collections to assign
// ---------------------------------------------------------------------------

const AssignPantriesPicker = ({ assignedIds, onDone, onClose }) => {
  const { collections, loading } = usePantryCollections();
  const [selected, setSelected]  = useState(new Set(assignedIds || []));

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <div className="bg-background rounded-large border border-text-tertiary/20 w-full max-w-lg max-h-[80vh] flex flex-col shadow-xl">
      <div className="p-lg border-b border-text-tertiary/10 flex items-center justify-between flex-shrink-0">
        <div>
          <h3 className="text-title-3 font-semibold text-primary">Assign Pantries</h3>
          <p className="text-caption text-text-secondary mt-xs">{selected.size} selected</p>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center">
          <X size={18} className="text-text-tertiary" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-lg">
        {loading ? (
          <div className="text-center py-xl"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" /></div>
        ) : collections.length === 0 ? (
          <div className="text-center py-xl">
            <Package size={32} className="text-text-tertiary/30 mx-auto mb-md" />
            <p className="text-callout font-semibold text-text-secondary mb-xs">No pantry collections</p>
            <p className="text-caption text-text-tertiary">Create collections in the Pantry tab to assign them here.</p>
          </div>
        ) : (
          <div className="space-y-sm">
            {collections.map(col => {
              const sel = selected.has(col.id);
              return (
                <button key={col.id} onClick={() => toggle(col.id)}
                  className={`w-full flex items-center gap-md p-md rounded-large border transition-all text-left ${sel ? 'border-primary/30 bg-primary/5' : 'border-text-tertiary/10 hover:border-text-tertiary/20'}`}>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${sel ? 'border-primary bg-primary' : 'border-text-tertiary/30'}`}>
                    {sel && <Check size={11} className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-callout font-semibold text-text-primary truncate">{col.name}</p>
                    <p className="text-caption text-text-secondary">{(col.productIds || []).length} products</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="p-lg border-t border-text-tertiary/10 flex gap-md flex-shrink-0">
        <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button variant="primary" onClick={() => { onDone(Array.from(selected)); onClose(); }} className="flex-1">
          Done ({selected.size})
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ProfileEditSheet — full-scroll form matching iOS CareProfileEditSheet
// ---------------------------------------------------------------------------

const ProfileEditSheet = ({ profile, onClose, onSave, uploadProfileImage }) => {
  const isEditing = !!profile;
  const imageRef  = useRef(null);

  const [name,          setName]          = useState(profile?.name          || '');
  const [relationship,  setRelationship]  = useState(profile?.relationship  || '');
  const [ageGroup,      setAgeGroup]      = useState(() => birthYearToAgeGroup(profile?.birthYear));
  const [notes,         setNotes]         = useState(profile?.notes         || '');
  const [tags,          setTags]          = useState(profile?.tags          || []);
  const [selectedAllergens, setSelectedAllergens] = useState(
    new Set((profile?.allergies || []).filter(a => COMMON_ALLERGENS.map(c => c.toLowerCase()).includes(a.toLowerCase())))
  );
  const [customAllergens,   setCustomAllergens]   = useState(
    (profile?.allergies || []).filter(a => !COMMON_ALLERGENS.map(c => c.toLowerCase()).includes(a.toLowerCase()))
  );
  const [avoidItems,        setAvoidItems]        = useState(profile?.dietaryRestrictions || []);
  const [assignedRecipeIds, setAssignedRecipeIds] = useState(profile?.assignedRecipeIds || []);
  const [assignedPantryIds, setAssignedPantryIds] = useState(profile?.assignedPantryIds || []);

  const [avoidSearch,       setAvoidSearch]       = useState('');
  const [avoidSuggestions,  setAvoidSuggestions]  = useState([]);
  const [showAvoidDropdown, setShowAvoidDropdown] = useState(false);
  const [customAllergenInput, setCustomAllergenInput] = useState('');
  const [showCustomAllergenInput, setShowCustomAllergenInput] = useState(false);

  const [showRecipePicker,  setShowRecipePicker]  = useState(false);
  const [showPantryPicker,  setShowPantryPicker]  = useState(false);

  const [imagePreview,  setImagePreview]  = useState(profile?.profileImageURL || null);
  const [uploading,     setUploading]     = useState(false);

  const [saving, setSaving] = useState(false);
  const isValid = name.trim().length > 0;

  // ── Avoid ingredient search ───────────────────────────────────────────────

  useEffect(() => {
    const search = async () => {
      if (avoidSearch.length >= 2) {
        const results = await ingredientSearchService.searchIngredients(avoidSearch);
        setAvoidSuggestions(results.slice(0, 10));
        setShowAvoidDropdown(true);
      } else {
        setAvoidSuggestions([]);
        setShowAvoidDropdown(false);
      }
    };
    const t = setTimeout(search, 300);
    return () => clearTimeout(t);
  }, [avoidSearch]);

  // ── Image upload ──────────────────────────────────────────────────────────

  const handleImageFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setImagePreview(preview);
    if (isEditing && profile?.id && uploadProfileImage) {
      setUploading(true);
      try { await uploadProfileImage(profile.id, file); }
      catch (err) { console.error(err); }
      finally { setUploading(false); }
    }
  };

  // ── Toggle dietary tag ────────────────────────────────────────────────────

  const toggleTag = (id) =>
    setTags(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);

  const toggleAllergen = (a) =>
    setSelectedAllergens(prev => {
      const next = new Set(prev);
      next.has(a) ? next.delete(a) : next.add(a);
      return next;
    });

  const addCustomAllergen = () => {
    const t = customAllergenInput.trim();
    if (t && !customAllergens.includes(t)) setCustomAllergens(prev => [...prev, t]);
    setCustomAllergenInput('');
    setShowCustomAllergenInput(false);
  };

  const addAvoidFromSuggestion = (ingredient) => {
    if (!avoidItems.includes(ingredient.name)) setAvoidItems(prev => [...prev, ingredient.name]);
    setAvoidSearch('');
    setShowAvoidDropdown(false);
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    const allAllergens = [
      ...Array.from(selectedAllergens).map(a => a.toLowerCase()),
      ...customAllergens.map(a => a.toLowerCase()),
    ];
    const data = {
      name:                name.trim(),
      relationship:        relationship || 'Other',
      birthYear:           ageGroupToBirthYear(ageGroup),
      tags,
      allergies:           allAllergens,
      dietaryRestrictions: avoidItems,
      notes:               notes.trim() || null,
      isAdmin:             profile?.isAdmin || false,
      assignedRecipeIds,
      assignedPantryIds,
      profileImageURL:     profile?.profileImageURL || null,
    };
    await onSave(profile?.id || null, data);
    setSaving(false);
    onClose();
  };

  return (
    <div className="bg-background rounded-large border border-text-tertiary/15 w-full max-w-2xl shadow-xl max-h-[92vh] flex flex-col">

      {/* Header */}
      <div className="flex items-center justify-between px-xl pt-xl pb-lg border-b border-text-tertiary/10 flex-shrink-0">
        <h2 className="text-title-2 font-rounded text-primary">
          {isEditing ? 'Edit Profile' : 'New Profile'}
        </h2>
        <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center">
          <X size={20} className="text-text-tertiary" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-xl py-lg space-y-lg">

        {/* ── Avatar card ── */}
        <div
          className="rounded-large p-xl flex flex-col items-center gap-md"
          style={{ background: 'linear-gradient(135deg, rgba(var(--color-primary-rgb),0.12), rgba(var(--color-primary-rgb),0.04))' }}
        >
          <div className="relative">
            <input ref={imageRef} type="file" accept="image/*" onChange={handleImageFile} className="hidden" />
            <AvatarCircle name={name || '?'} size={96} imageURL={imagePreview} />
            <button
              onClick={() => imageRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center ring-2 ring-white disabled:opacity-50"
            >
              {uploading
                ? <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                : <Camera size={14} className="text-white" />
              }
            </button>
          </div>
          {name && (
            <p className="text-callout font-semibold text-primary">{name}</p>
          )}
        </div>

        {/* ── About (name, relationship, age group) ── */}
        <FormCard title="About" icon={User}>
          <div className="space-y-md">
            {/* Name */}
            <div
              className="flex items-center gap-sm px-md py-sm rounded-large"
              style={{ background: 'rgba(var(--color-primary-rgb),0.04)' }}
            >
              <User size={16} className="text-text-secondary flex-shrink-0" />
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Name *"
                autoFocus
                className="flex-1 bg-transparent text-body text-text-primary focus:outline-none"
              />
            </div>

            {/* Relationship chip grid */}
            <div>
              <p className="text-caption text-text-secondary mb-sm flex items-center gap-xs">
                <Users size={13} /> Relationship
                {relationship && <span className="ml-auto text-caption font-semibold text-primary">{relationship}</span>}
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-sm">
                {RELATIONSHIPS.map(rel => {
                  const sel = relationship === rel;
                  return (
                    <button
                      key={rel}
                      onClick={() => setRelationship(sel ? '' : rel)}
                      className={`py-sm rounded-large text-caption font-semibold transition-all ${
                        sel ? 'bg-primary text-white' : 'text-primary border border-primary/18'
                      }`}
                      style={!sel ? { background: 'rgba(var(--color-primary-rgb),0.04)' } : {}}
                    >
                      {rel}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Age group picker */}
            <div>
              <p className="text-caption text-text-secondary mb-sm flex items-center gap-xs">
                Age group
                {ageGroup && <span className="ml-auto text-caption font-semibold text-primary">{ageGroup}</span>}
              </p>
              <div className="grid grid-cols-5 gap-sm">
                {AGE_GROUPS.map(({ label, subtitle }) => {
                  const sel = ageGroup === label;
                  return (
                    <button
                      key={label}
                      onClick={() => setAgeGroup(sel ? null : label)}
                      className={`flex flex-col items-center py-sm px-xs rounded-large transition-all ${
                        sel ? 'bg-primary text-white' : 'text-primary border border-primary/12'
                      }`}
                      style={!sel ? { background: 'rgba(var(--color-primary-rgb),0.04)' } : {}}
                    >
                      <span className={`text-caption font-semibold ${sel ? 'text-white' : 'text-text-primary'}`}>{label}</span>
                      <span className={`text-[10px] mt-xs ${sel ? 'text-white/80' : 'text-text-tertiary'}`}>{subtitle}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </FormCard>

        {/* ── Dietary Preferences ── */}
        <FormCard title="Dietary Preferences" icon={Leaf}>
          <div className="grid grid-cols-2 gap-sm">
            {DIETARY_TAGS.map(tag => {
              const sel = tags.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  onClick={() => toggleTag(tag.id)}
                  className="flex items-center gap-sm py-sm px-md rounded-large text-caption font-semibold transition-all"
                  style={{
                    color:      sel ? '#fff'          : tag.color,
                    background: sel ? tag.color        : `${tag.color}1A`,
                    border:     sel ? 'none'           : `1px solid ${tag.color}40`,
                  }}
                >
                  <span className="flex-1 text-left">{tag.name}</span>
                  {sel && <Check size={12} />}
                </button>
              );
            })}
          </div>
        </FormCard>

        {/* ── Allergens ── */}
        <FormCard title="Allergens" icon={AlertTriangle} tint="#ef4444">
          <div className="space-y-md">
            {/* Common allergen chips */}
            <div className="flex flex-wrap gap-sm">
              {COMMON_ALLERGENS.map(allergen => {
                const sel = selectedAllergens.has(allergen);
                return (
                  <button
                    key={allergen}
                    onClick={() => toggleAllergen(allergen)}
                    className={`flex items-center gap-xs px-md py-xs rounded-full text-caption font-semibold transition-all ${
                      sel ? 'bg-error text-white' : 'text-error border border-error/25'
                    }`}
                    style={!sel ? { background: 'rgba(var(--color-error-rgb),0.07)' } : {}}
                  >
                    {sel && <Check size={10} />}
                    {allergen}
                  </button>
                );
              })}
            </div>

            {/* Custom allergens */}
            {customAllergens.length > 0 && (
              <div className="flex flex-wrap gap-xs">
                {customAllergens.map(a => (
                  <span
                    key={a}
                    className="flex items-center gap-xs px-md py-xs rounded-full text-caption font-semibold text-error"
                    style={{ background: 'rgba(var(--color-error-rgb),0.08)' }}
                  >
                    {a}
                    <button onClick={() => setCustomAllergens(prev => prev.filter(x => x !== a))}>
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add custom allergen */}
            {showCustomAllergenInput ? (
              <div className="flex gap-sm">
                <input
                  type="text"
                  value={customAllergenInput}
                  onChange={e => setCustomAllergenInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addCustomAllergen()}
                  placeholder="e.g. Mustard"
                  autoFocus
                  className="flex-1 px-md py-sm bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body"
                />
                <Button variant="outline" size="small" onClick={() => { setShowCustomAllergenInput(false); setCustomAllergenInput(''); }}>Cancel</Button>
                <Button variant="primary" size="small" onClick={addCustomAllergen} disabled={!customAllergenInput.trim()}>Add</Button>
              </div>
            ) : (
              <button
                onClick={() => setShowCustomAllergenInput(true)}
                className="flex items-center gap-xs text-caption font-semibold text-error hover:text-error/80 transition-colors"
              >
                <Plus size={13} />
                Add custom allergen
              </button>
            )}
          </div>
        </FormCard>

        {/* ── Ingredients to Avoid ── */}
        <FormCard title="Ingredients to Avoid" icon={X} tint="#f97316">
          <div className="space-y-md">
            {avoidItems.length > 0 && (
              <div className="flex flex-wrap gap-xs">
                {avoidItems.map(item => (
                  <span
                    key={item}
                    className="flex items-center gap-xs px-md py-xs rounded-full text-caption font-semibold"
                    style={{ color: '#f97316', background: 'rgba(249,115,22,0.08)' }}
                  >
                    {item.charAt(0).toUpperCase() + item.slice(1)}
                    <button onClick={() => setAvoidItems(prev => prev.filter(a => a !== item))}>
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {avoidItems.length === 0 && (
              <p className="text-caption text-text-tertiary italic">None added</p>
            )}

            {/* Search */}
            <div className="relative">
              <div className="relative">
                <Search size={15} className="absolute left-md top-1/2 -translate-y-1/2 text-text-tertiary" />
                <input
                  type="text"
                  value={avoidSearch}
                  onChange={e => setAvoidSearch(e.target.value)}
                  placeholder="Search ingredients to avoid..."
                  className="w-full pl-xxxl pr-md py-sm bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body"
                />
              </div>
              {showAvoidDropdown && avoidSuggestions.length > 0 && (
                <div className="absolute z-10 w-full bg-surface border border-text-tertiary/20 rounded-medium shadow-lg max-h-48 overflow-y-auto mt-xs">
                  {avoidSuggestions.map((ingredient, i) => (
                    <button
                      key={i}
                      onClick={() => addAvoidFromSuggestion(ingredient)}
                      className="w-full px-md py-sm text-left hover:bg-primary/8 transition-colors flex items-center justify-between"
                    >
                      <p className="text-callout text-text-primary">{ingredient.name}</p>
                      <Plus size={14} className="text-primary" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </FormCard>

        {/* ── Assigned Recipes ── */}
        <FormCard title="Assigned Recipes" icon={BookOpen}>
          <div className="space-y-sm">
            {assignedRecipeIds.length === 0
              ? <p className="text-caption text-text-tertiary italic">No recipes assigned</p>
              : <p className="text-callout font-semibold text-primary">{assignedRecipeIds.length} recipe{assignedRecipeIds.length !== 1 ? 's' : ''} assigned</p>
            }
            <button
              onClick={() => setShowRecipePicker(true)}
              className="w-full flex items-center justify-center gap-sm py-sm rounded-large text-callout font-semibold transition-all"
              style={{ color: 'var(--color-primary)', background: 'rgba(var(--color-primary-rgb),0.07)', border: '1px solid rgba(var(--color-primary-rgb),0.18)' }}
            >
              <Plus size={15} />
              Manage assigned recipes
            </button>
          </div>
        </FormCard>

        {/* ── Assigned Pantries ── */}
        <FormCard title="Assigned Pantries" icon={Package} tint="#5B7BCC">
          <div className="space-y-sm">
            {assignedPantryIds.length === 0
              ? <p className="text-caption text-text-tertiary italic">No pantry collections assigned</p>
              : <p className="text-callout font-semibold" style={{ color: '#5B7BCC' }}>{assignedPantryIds.length} collection{assignedPantryIds.length !== 1 ? 's' : ''} assigned</p>
            }
            <button
              onClick={() => setShowPantryPicker(true)}
              className="w-full flex items-center justify-center gap-sm py-sm rounded-large text-callout font-semibold transition-all"
              style={{ color: '#5B7BCC', background: 'rgba(91,123,204,0.07)', border: '1px solid rgba(91,123,204,0.18)' }}
            >
              <Plus size={15} />
              Manage assigned pantries
            </button>
          </div>
        </FormCard>

        {/* ── Notes ── */}
        <FormCard title="Notes" icon={Pencil} tint="var(--color-text-secondary)">
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Optional notes (e.g. EpiPen required)..."
            rows={3}
            className="w-full px-md py-sm rounded-large resize-none text-body text-text-primary focus:outline-none focus:border-primary border border-text-tertiary/15"
            style={{ background: 'rgba(var(--color-primary-rgb),0.03)' }}
          />
        </FormCard>

      </div>

      {/* Footer */}
      <div className="flex gap-md px-xl pb-xl pt-lg border-t border-text-tertiary/10 flex-shrink-0">
        <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={!isValid || saving} className="flex-1">
          {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Profile'}
        </Button>
      </div>

      {/* Pickers */}
      {showRecipePicker && (
        <Modal onBackdropClick={() => setShowRecipePicker(false)}>
          <AssignRecipesPicker
            assignedIds={assignedRecipeIds}
            onDone={ids => setAssignedRecipeIds(ids)}
            onClose={() => setShowRecipePicker(false)}
          />
        </Modal>
      )}
      {showPantryPicker && (
        <Modal onBackdropClick={() => setShowPantryPicker(false)}>
          <AssignPantriesPicker
            assignedIds={assignedPantryIds}
            onDone={ids => setAssignedPantryIds(ids)}
            onClose={() => setShowPantryPicker(false)}
          />
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// CareProfileCard — colored left-border card with collapse/expand
// Matches iOS CareProfileCard
// ---------------------------------------------------------------------------

const CareProfileCard = ({ profile, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const color = getAvatarColor(profile.name);

  const allergenCount = (profile.allergies || []).length;
  const avoidCount    = (profile.dietaryRestrictions || []).length;
  const tagCount      = (profile.tags || []).length;
  const hasDetails    = allergenCount > 0 || avoidCount > 0 || tagCount > 0;

  const tagObj = (id) => DIETARY_TAGS.find(t => t.id === id);

  return (
    <div
      className="rounded-large bg-white overflow-hidden relative"
      style={{ border: `1.5px solid ${color}33`, boxShadow: `0 4px 10px ${color}1A` }}
    >
      {/* Left accent border — matches iOS overlay */}
      <div
        className="absolute left-0 top-4 bottom-4 w-1 rounded-full"
        style={{ background: color }}
      />

      {/* Header */}
      <div className="flex items-center gap-md pl-5 pr-md py-md">
        <AvatarCircle name={profile.name} size={52} imageURL={profile.profileImageURL} />

        <div className="flex-1 min-w-0">
          <p className="text-callout font-bold text-text-primary">{profile.name}</p>
          {profile.relationship && (
            <p className="text-caption text-text-secondary capitalize">{profile.relationship}</p>
          )}
        </div>

        <div className="flex items-center gap-sm flex-shrink-0">
          {hasDetails && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="w-8 h-8 flex items-center justify-center text-text-tertiary hover:text-text-primary transition-colors"
            >
              {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          )}
          <button onClick={onEdit}   className="w-8 h-8 rounded-full hover:bg-primary/10  flex items-center justify-center"><Edit2  size={15} className="text-primary" /></button>
          {!profile.isAdmin && (
            <button onClick={onDelete} className="w-8 h-8 rounded-full hover:bg-error/10   flex items-center justify-center"><Trash2 size={15} className="text-error"   /></button>
          )}
        </div>
      </div>

      {/* Collapsed summary pills */}
      {!expanded && hasDetails && (
        <>
          <div className="border-t border-text-tertiary/10 mx-md opacity-40" />
          <div className="flex flex-wrap gap-sm pl-5 pr-md py-sm">
            {tagCount > 0 && (
              <span className="flex items-center gap-xs px-sm py-xs rounded-full text-caption font-semibold text-primary" style={{ background: 'rgba(var(--color-primary-rgb),0.08)' }}>
                <Leaf size={10} />{tagCount} tag{tagCount !== 1 ? 's' : ''}
              </span>
            )}
            {allergenCount > 0 && (
              <span className="flex items-center gap-xs px-sm py-xs rounded-full text-caption font-semibold text-error" style={{ background: 'rgba(var(--color-error-rgb),0.08)' }}>
                <AlertTriangle size={10} />{allergenCount} allergen{allergenCount !== 1 ? 's' : ''}
              </span>
            )}
            {avoidCount > 0 && (
              <span className="flex items-center gap-xs px-sm py-xs rounded-full text-caption font-semibold" style={{ color: '#f97316', background: 'rgba(249,115,22,0.08)' }}>
                <X size={10} />{avoidCount} avoided
              </span>
            )}
          </div>
        </>
      )}

      {/* Expanded detail */}
      {expanded && (
        <>
          <div className="border-t border-text-tertiary/10 mx-md opacity-40" />
          <div className="pl-5 pr-md py-md space-y-md">

            {tagCount > 0 && (
              <div>
                <p className="text-[10px] font-bold text-primary uppercase tracking-wide mb-sm">Diet Tags</p>
                <div className="flex flex-wrap gap-xs">
                  {profile.tags.map(id => {
                    const t = tagObj(id);
                    return t ? (
                      <span key={id} className="px-sm py-xs rounded-full text-caption font-semibold"
                        style={{ color: t.color, background: `${t.color}1A` }}>
                        {t.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {allergenCount > 0 && (
              <div>
                <p className="text-[10px] font-bold text-error uppercase tracking-wide mb-sm">Allergens</p>
                <div className="flex flex-wrap gap-xs">
                  {profile.allergies.map(a => (
                    <span key={a} className="px-sm py-xs rounded-full text-caption font-semibold text-error" style={{ background: 'rgba(var(--color-error-rgb),0.08)' }}>
                      {a.charAt(0).toUpperCase() + a.slice(1)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {avoidCount > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide mb-sm" style={{ color: '#f97316' }}>Avoiding</p>
                <div className="flex flex-wrap gap-xs">
                  {profile.dietaryRestrictions.map(item => (
                    <span key={item} className="px-sm py-xs rounded-full text-caption font-semibold" style={{ color: '#f97316', background: 'rgba(249,115,22,0.08)' }}>
                      {item.charAt(0).toUpperCase() + item.slice(1)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(profile.assignedRecipeIds || []).length > 0 && (
              <div>
                <p className="text-[10px] font-bold text-primary uppercase tracking-wide mb-xs">Assigned Recipes</p>
                <p className="text-caption text-text-secondary">{profile.assignedRecipeIds.length} recipe{profile.assignedRecipeIds.length !== 1 ? 's' : ''} assigned</p>
              </div>
            )}

            {(profile.assignedPantryIds || []).length > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide mb-xs" style={{ color: '#5B7BCC' }}>Assigned Pantries</p>
                <p className="text-caption text-text-secondary">{profile.assignedPantryIds.length} collection{profile.assignedPantryIds.length !== 1 ? 's' : ''} assigned</p>
              </div>
            )}

            {profile.notes && (
              <div>
                <p className="text-[10px] font-bold text-text-secondary uppercase tracking-wide mb-xs">Notes</p>
                <p className="text-caption text-text-secondary">{profile.notes}</p>
              </div>
            )}

            {!hasDetails && (
              <p className="text-caption text-text-tertiary italic">No dietary restrictions set</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// CareProfilesHub — main view
// Matches iOS CareHubView
// ---------------------------------------------------------------------------

const CareProfilesHub = () => {
  const {
    displayProfiles, loading,
    groupName, loadGroupName, saveGroupName,
    createProfile, updateProfile, deleteProfile,
    uploadProfileImage,
  } = useCareProfiles();

  
  const [showForm,        setShowForm]        = useState(false);
  const [editingProfile,  setEditingProfile]  = useState(null);
  const [deletingProfile, setDeletingProfile] = useState(null);
  const [deleting,        setDeleting]        = useState(false);

  // Group name edit
  const [isEditingName,  setIsEditingName]  = useState(false);
  const [groupNameDraft, setGroupNameDraft] = useState('');

  useEffect(() => { loadGroupName(); }, [loadGroupName]);

  const openCreate = () => { setEditingProfile(null); setShowForm(true); };
  const openEdit   = (p)  => { setEditingProfile(p);  setShowForm(true); };

  const handleSave = async (profileId, data) => {
    if (profileId) {
      await updateProfile(profileId, data);
    } else {
      await createProfile(data);
    }
  };

  const handleDelete = async () => {
    if (!deletingProfile) return;
    setDeleting(true);
    try { await deleteProfile(deletingProfile.id); }
    catch (err) { console.error(err); }
    finally { setDeleting(false); setDeletingProfile(null); }
  };

  if (loading) {
    return (
      <div className="text-center py-xxxl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto mb-md" />
        <p className="text-callout text-text-secondary">Loading care profiles...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-lg">

      {/* ── Group name header — matches iOS hubNameHeader ── */}
      <div className="flex items-center gap-sm">
        {isEditingName ? (
          <>
            <input
              type="text"
              value={groupNameDraft}
              onChange={e => setGroupNameDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setIsEditingName(false); saveGroupName(groupNameDraft); } }}
              placeholder="e.g. Smith Family"
              autoFocus
              className="flex-1 px-md py-sm bg-white border-2 border-primary/25 rounded-large text-title-3 font-bold text-text-primary focus:outline-none"
            />
            <button
              onClick={() => { setIsEditingName(false); saveGroupName(groupNameDraft); }}
              className="px-lg py-sm rounded-full bg-primary text-white text-caption font-semibold"
            >
              Save
            </button>
            <button onClick={() => { setIsEditingName(false); setGroupNameDraft(groupName); }}
              className="w-8 h-8 flex items-center justify-center text-text-tertiary">
              <X size={18} />
            </button>
          </>
        ) : (
          <>
            <h1 className="text-display font-rounded text-primary flex-1">
              {groupName || 'Care Hub'}
            </h1>
            <button
              onClick={() => { setGroupNameDraft(groupName); setIsEditingName(true); }}
              className="w-9 h-9 rounded-full hover:bg-primary/10 flex items-center justify-center transition-colors"
            >
              <Pencil size={16} className="text-primary" />
            </button>
            <Button variant="primary" onClick={openCreate}>
              <Plus size={16} className="mr-xs" />
              Add Profile
            </Button>
          </>
        )}
      </div>

      <p className="text-body text-text-secondary -mt-sm">
        Manage dietary needs and restrictions for family members
      </p>

      {/* ── Care Profiles section header ── */}
      {displayProfiles.length > 0 && (
        <div className="flex items-center gap-sm">
          <div
            className="w-8 h-8 rounded-large flex items-center justify-center"
            style={{ background: 'rgba(var(--color-primary-rgb),0.12)' }}
          >
            <Users size={14} className="text-primary" />
          </div>
          <h2 className="text-title-3 font-bold text-text-primary flex-1">Care Profiles</h2>
          <span className="text-caption text-text-tertiary">
            {displayProfiles.length} profile{displayProfiles.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* ── Profile list or empty state ── */}
      {displayProfiles.length === 0 ? (
        <div
          className="rounded-large p-xxxl text-center"
          style={{ background: 'linear-gradient(135deg, rgba(var(--color-surface),1), rgba(var(--color-primary-rgb),0.05))', border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
        >
          <div className="w-20 h-20 rounded-full bg-primary/8 flex items-center justify-center mx-auto mb-lg">
            <User size={36} className="text-primary" style={{ opacity: 0.5 }} />
          </div>
          <h3 className="text-title-3 font-bold text-text-primary mb-sm">No care profiles yet</h3>
          <p className="text-body text-text-secondary mb-lg max-w-md mx-auto">
            Add family members to track dietary needs, allergies, and preferences for everyone
          </p>
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} className="mr-xs" />
            Add Your First Profile
          </Button>
        </div>
      ) : (
        <div className="space-y-md">
          {displayProfiles.map(profile => (
            <CareProfileCard
              key={profile.id}
              profile={profile}
              onEdit={() => openEdit(profile)}
              onDelete={() => setDeletingProfile(profile)}
            />
          ))}
        </div>
      )}

      {/* ── Profile form modal ── */}
      {showForm && (
        <Modal onBackdropClick={() => setShowForm(false)}>
          <ProfileEditSheet
            profile={editingProfile}
            onClose={() => setShowForm(false)}
            onSave={handleSave}
            uploadProfileImage={uploadProfileImage}
          />
        </Modal>
      )}

      {/* ── Delete confirmation modal ── */}
      {deletingProfile && (
        <Modal onBackdropClick={() => setDeletingProfile(null)}>
          <div className="bg-background rounded-large border border-text-tertiary/15 w-full max-w-sm p-xl text-center shadow-xl">
            <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-md">
              <Trash2 size={24} className="text-error" />
            </div>
            <h3 className="text-title-3 font-bold text-text-primary mb-sm">Delete Profile?</h3>
            <p className="text-body text-text-secondary mb-lg">
              Permanently delete "{deletingProfile.name}"? This cannot be undone.
            </p>
            <div className="flex gap-sm">
              <Button variant="outline" className="flex-1" onClick={() => setDeletingProfile(null)} disabled={deleting}>Cancel</Button>
              <Button variant="primary" className="flex-1 bg-error border-error hover:bg-error/90" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CareProfilesHub;